import React from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Checkbox from '@mui/material/Checkbox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { get } from '../../utils/service'
import { toReadableTimestamp } from '../../utils/dateUtils'
import Alert from '../alert/alert'
import { Profile } from '../../types/profile'
import ProfileDiff from './profileDiff'
import { convertProfileToDisplayUnits } from '../../utils/profileEditorUtils'

interface ProfileHistoryProps {
  profileData: Profile
}

const ProfileHistory = (props: ProfileHistoryProps): JSX.Element => {
  const params = useParams()
  const { t } = useTranslation()

  const [checkedProfiles, setCheckedProfiles] = React.useState<Array<Profile>>([])
  const [profileHistory, setProfileHistory] = React.useState<Array<Profile> | null>(null)
  const [hasProfileHistoryError, setHasProfileHistoryError] = React.useState<boolean>(false)
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = React.useState<boolean>(false)
  const [isShowingDiff, setIsShowingDiff] = React.useState<boolean>(false)

  // when profileId is changed (after save, reset data so new data is loaded when history button is clicked)
  React.useEffect(() => {
    setProfileHistory(null)
    setHasProfileHistoryError(false)
    setIsShowingDiff(false)
    setCheckedProfiles([])
  }, [props.profileData.id])

  const loadProfileHistory = (): void => {
    setIsHistoryDialogOpen(true)
    setHasProfileHistoryError(false)

    if (!profileHistory || hasProfileHistoryError) {
      get<Array<Profile>>(`profiles/${props.profileData.id}/parents/`)
        .then((result) => setProfileHistory(result.data.map((x) => convertProfileToDisplayUnits(x))))
        .catch(() => setHasProfileHistoryError(true))
    }
  }

  const generateProfileRow = (profile: Profile): JSX.Element => {
    const currentVersionFragment =
      profile.id === props.profileData.id ? ` (${t('editprofileview.currentversion')})` : ''
    const text = `${profile.name} - ${toReadableTimestamp(profile.modified)}${currentVersionFragment}`
    return (
      <Stack key={profile.id} direction="row" alignItems="center">
        <Checkbox
          name={profile.url}
          checked={!!checkedProfiles.find((x) => x.id === profile.id)}
          disabled={!checkedProfiles.find((x) => x.id === profile.id) && checkedProfiles.length > 1}
          onChange={(_event: React.ChangeEvent<HTMLInputElement>, newCheckedState: boolean) => {
            if (newCheckedState) {
              setCheckedProfiles((oldProfiles) => {
                const newProfiles = [profile, ...oldProfiles]
                const sortedProfiles = newProfiles.sort(
                  (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
                )
                return sortedProfiles
              })
            } else {
              setCheckedProfiles((oldProfiles) => oldProfiles.filter((x) => x.id !== profile.id))
            }
          }}
        />
        {profile.id === props.profileData.id ? (
          <Typography>{text}</Typography>
        ) : (
          <Link href={`/machine/${params.machineId}/profiles/${profile.id}`}>{text}</Link>
        )}
      </Stack>
    )
  }

  return (
    <>
      <Button variant="outlined" onClick={loadProfileHistory}>
        {t('editprofileview.profilehistory')}
      </Button>
      <Dialog
        fullWidth
        open={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>{t('editprofileview.previousversions')}</DialogTitle>
        <DialogContent>
          {hasProfileHistoryError && <Alert severity="error">{t('editprofileview.profilehistoryerror')}</Alert>}
          {!profileHistory && !hasProfileHistoryError && <CircularProgress />}
          {profileHistory &&
            !isShowingDiff &&
            [props.profileData, ...(profileHistory || [])].map((x) => generateProfileRow(x))}
          {profileHistory && isShowingDiff && <ProfileDiff selectedProfiles={checkedProfiles} />}
        </DialogContent>
        <DialogActions>
          {!isShowingDiff && (
            <Button disabled={checkedProfiles.length < 2} onClick={(): void => setIsShowingDiff(true)}>
              {t('editprofileview.showdifferences')}
            </Button>
          )}
          {isShowingDiff && <Button onClick={(): void => setIsShowingDiff(false)}>{t('editprofileview.back')}</Button>}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ProfileHistory
